import { useCallback, useMemo, type ReactNode } from "react";
import { toast } from "sonner";

import { describeSafeErrorMessage } from "@/lib/trpc-errors";
import { Toaster } from "@/shared/ui/sonner";
import { ToastContext, type ToastContextValue } from "./toast-context";

type ToastProviderProps = {
  children: ReactNode;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const showInfo = useCallback<ToastContextValue["showInfo"]>((title, options) => {
    toast.info(title, {
      description: options?.description,
    });
  }, []);

  const showError = useCallback<ToastContextValue["showError"]>((title, options) => {
    toast.error(title, {
      description: options?.description,
    });
  }, []);

  const showMutationError = useCallback<ToastContextValue["showMutationError"]>((title, error) => {
    toast.error(title, {
      description: describeSafeErrorMessage(error),
    });
  }, []);

  const showSuccess = useCallback<ToastContextValue["showSuccess"]>((title, options) => {
    toast.success(title, {
      description: options?.description,
    });
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      showInfo,
      showError,
      showMutationError,
      showSuccess,
    }),
    [showError, showInfo, showMutationError, showSuccess],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Top-CENTER, not top-right. Sonner's viewport sits 24px from the top of
          the window at a z-index far above the sticky admin header's, so a
          top-right toast covered the workspace switcher and the account menu —
          exactly the controls someone reaches for after an action fails.
          Centering moves the stack over the header's search field instead,
          which has no controls a toast can swallow, and keeps toasts at the top
          of the viewport rather than pushing every one of them down the page. */}
      <Toaster position="top-center" />
    </ToastContext.Provider>
  );
};
